import type { Icon as PhosphorIcon } from '@phosphor-icons/react';

/**
 * Visual de fila (tile de icono coloreado) compartido por las listas del admin.
 * Cada pantalla mapea su dominio (tipo, canal, categoría…) a uno de estos pares
 * accent+bg suaves. Ver components/RowTile para el render.
 */
export interface RowVisual {
  Icon: PhosphorIcon;
  accent: string;
  bg: string;
}

/**
 * Pares accent+bg suaves compartidos (rediseño 2, 2026-09-25).
 *
 * Recalibrados a la rampa fría del documento de diseño. Antes eran los tonos
 * de la paleta cálida (#7A4A22 sobre #F5EAE0 y compañía), que sobre el fondo
 * gris-azulado nuevo se leen amarillentos: un tile beige sobre #F5F6F7 parece
 * un error de color, no una decisión.
 *
 * Seis de los diez salen literalmente del array `TILES` del documento, que es
 * el que usa para los avatares y las pastillas. Los otros cuatro se derivan
 * con el mismo criterio —croma bajo, texto oscuro— para cubrir las categorías
 * que el documento no dibuja.
 *
 * Los nombres se conservan porque son la API que usan las pantallas, aunque
 * describan el tono solo aproximadamente.
 *
 * Todos los accent superan 4.5:1 sobre su propio `bg` y sobre blanco — muy por
 * encima del 3:1 que pide un elemento gráfico.
 *
 * ⚠️ Y no hay dos entradas iguales, que es más fácil de romper de lo que
 * parece: al recalibrar, `sky` y `teal` salieron con el MISMO par y las dos
 * categorías que los usan habrían quedado indistinguibles — sin que nada
 * fallara, porque el contraste seguía en verde. El tile ES la señal de
 * categoría; dos iguales es una señal que no señala.
 */
export const TILE_COLORS = {
  indigo: { accent: '#4B4AC2', bg: '#ECECFB' },
  blue:   { accent: '#001D33', bg: '#E6EAEE' },
  sky:    { accent: '#1A6A9E', bg: '#E2EFF7' },
  teal:   { accent: '#0E7285', bg: '#E3F4F7' },
  green:  { accent: '#2F6B41', bg: '#E6F1E9' },
  amber:  { accent: '#8A5A16', bg: '#FAF0DC' },
  brown:  { accent: '#7A4A22', bg: '#F5EAE0' },
  pink:   { accent: '#9D2F76', bg: '#FCE7F2' },
  purple: { accent: '#6B3FA0', bg: '#F0E9F9' },
  slate:  { accent: '#40494F', bg: '#F1F2F4' },
} as const;
